import { useState, useEffect } from 'react';
//import { Auth } from 'aws-amplify';
import {
  signIn,
  signOut,
  getCurrentUser,
  fetchAuthSession,
  resetPassword,
  forgotPassword,
  confirmResetPassword
} from '@aws-amplify/auth';
import InputForm from '../../../components/form/InputForm';
import bgImage from './../../../assets/images/whisperbi_fondo_login.jpg';
import { Link, useNavigate, useParams } from 'react-router-dom';
import ErrorAlert from '../../../components/common/alerts/ErrorAlert';
import Loader from "../../../components/common/loaders/Loader";
import { CheckIcon } from '../../../assets/icons';
import { Tooltip } from 'react-tooltip';
import {
  containsNumbers,
  containsCapitalLetters,
  containsLowercaseLetters,
  containsSpecialChars
} from '../../../utils';

const PasswordRecovery = () => {
  const navigate = useNavigate();
  const { type } = useParams();

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState(1);
  const [progress, setProgress] = useState(0);

  // password validations
  const [hasNumbers, setHasNumbers] = useState(false);
  const [hasCapitalLetters, setHasCapitalLetters] = useState(false);
  const [hasLowercaseLetters, setHasLowercaseLetters] = useState(false);
  const [hasSpecialCharacters, setHasSpecialCharacters] = useState(false);
  const [hasEightChars, setHasEightChars] = useState(false);
  const [isValidPassword, setIsValidPassword] = useState(false);

  const validatePassword = (password, confirmPassword, code) => {
    const validation = {
      hasNumbers: containsNumbers(password),
      hasCapitalLetters: containsCapitalLetters(password),
      hasLowercaseLetters: containsLowercaseLetters(password),
      hasSpecialCharacters: containsSpecialChars(password),
      hasEightCharacters: password.length >= 8,
    };

    const isValid =
      validation.hasNumbers &&
      validation.hasCapitalLetters &&
      validation.hasLowercaseLetters &&
      validation.hasSpecialCharacters &&
      validation.hasEightCharacters &&
      password === confirmPassword &&
      code.length > 2;

    return { validation, isValid };
  };

  const getConfirmPasswordClass = (password, confirmPassword) => {
    if (confirmPassword.length < 7) return 'hidden';
    if (password === confirmPassword) return 'hidden';
    return 'animate__animated animate__fadeIn';
  };

  useEffect(() => {
    const { validation, isValid } = validatePassword(password, confirmPassword, code);
    setHasNumbers(validation.hasNumbers);
    setHasCapitalLetters(validation.hasCapitalLetters);
    setHasLowercaseLetters(validation.hasLowercaseLetters);
    setHasSpecialCharacters(validation.hasSpecialCharacters);
    setHasEightChars(validation.hasEightCharacters);
    setIsValidPassword(isValid);
  }, [password, confirmPassword, code]);

  useEffect(() => {
    if (step === 3) {
      const intervalId = setInterval(() => setProgress((p) => p + 1), 40);
      if (progress === 100) navigate('/login');
      return () => clearInterval(intervalId);
    }
  }, [step, progress, navigate]);

  // Paso 1: enviar código
  const handleForgotPassword = async () => {
    setLoading(true);
    try {
      await resetPassword({ username: email.trim() });
      setError('');
      setStep(2);
    } catch (err) {
      setError(err.message ?? 'Error al enviar el código');
      setEmail('');
      console.error('handleForgotPassword err:', err);
    } finally {
      setLoading(false);
    }
  };

  // Paso 2: confirmar nueva contraseña
  const handlePasswordResetSubmit = async () => {
    if (password !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }
    setLoading(true);
    try {
      await confirmResetPassword({
        username: email.trim(),
        confirmationCode: code.trim(),
        newPassword: password,
      });
      setStep(3);
    } catch (err) {
      setError(err.message ?? 'No se pudo restablecer la contraseña');
      console.error('handlePasswordResetSubmit err:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <div className="flex h-screen bg-black text-white">
      {/* Columna izquierda (form) */}
      <section className="w-full lg:w-1/2 h-full mx-auto">
        <div className="flex flex-col items-center justify-center px-6 py-8 mx-auto md:h-screen lg:py-0">
          <div className="w-full rounded-2xl border border-orange-500/60 bg-zinc-900/70 backdrop-blur shadow-xl md:mt-0 sm:max-w-md xl:p-0">
            <div className="p-6 sm:p-8 space-y-6">

              {step === 1 && (
                <div className="space-y-8">
                  <div className="flex mb-2">
                    {type === 'forgot-password' && (
                      <Link
                        to="/login"
                        className="h-8 w-8 flex items-center justify-center rounded-full border border-orange-500/60 text-orange-500 hover:text-orange-400 hover:border-orange-400 transition"
                        aria-label="Volver a iniciar sesión"
                      >
                        <svg
                          fill="none"
                          className="w-4 h-4"
                          stroke="currentColor"
                          strokeWidth="2"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                          aria-hidden="true"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
                        </svg>
                      </Link>
                    )}
                    <div className="w-full flex justify-center text-center">
                      <h1 className="text-2xl md:text-3xl font-bold">
                        {type === 'forgot-password' ? '¿Olvidaste tu contraseña?' : 'Cambiar contraseña'}
                      </h1>
                    </div>
                  </div>
                  <span className="block mx-auto h-1 w-16 bg-orange-500 rounded"></span>

                  <div className="space-y-4">
                    <p className="text-sm text-gray-300">
                      {type === 'forgot-password'
                        ? 'Ingresa tu correo electrónico y te enviaremos un código para que recuperes el acceso a tu cuenta.'
                        : 'Ingresa tu correo electrónico y te enviaremos un código para crear tu nueva contraseña.'}
                    </p>

                    <InputForm
                      name="email"
                      placeholder="john@email.com"
                      type="email"
                      title=""
                      value={email}
                      setValue={setEmail}
                    />

                    {error && <ErrorAlert error={error} />}

                    <div>
                      <button
                        onClick={handleForgotPassword}
                        disabled={email.length <= 3}
                        className="w-full py-3 font-semibold rounded-full bg-black text-white border-2 border-orange-500 enabled:hover:bg-black/90 enabled:hover:border-orange-400 enabled:cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-orange-500 transition"
                      >
                        Enviar código
                      </button>
                    </div>

                    <hr className="border-zinc-700" />
                  </div>
                </div>
              )}

              {step === 2 && (
                <div className="space-y-6">
                  <div className="text-center">
                    <h1 className="text-2xl md:text-3xl font-bold">Restablecer contraseña</h1>
                    <span className="block mx-auto mt-2 h-1 w-16 bg-orange-500 rounded"></span>
                  </div>

                  <div className="space-y-4">
                    <p className="text-sm text-gray-300 text-center">
                      Te enviamos un código para restablecer tu contraseña. Por favor, ingrésalo en el siguiente campo.
                    </p>

                    <InputForm
                      name="code"
                      placeholder="Ingresa el código"
                      type="text"
                      value={code}
                      setValue={setCode}
                    />

                    <InputForm
                      name="password"
                      placeholder="••••••••"
                      type="password"
                      title="Ingresa tu nueva contraseña"
                      value={password}
                      setValue={setPassword}
                    />

                    {/* Reglas de contraseña */}
                    <div className={`pl-4 ${!password ? "hidden" : "animate__animated animate__fadeIn"}`}>
                      <ul className="text-xs text-gray-300">
                        <li className="flex items-center">
                          <span className="mr-1"><CheckIcon isChecked={hasEightChars} /></span>
                          Longitud mínima de 8 caracteres
                        </li>
                        <li className="flex items-center">
                          <span className="mr-1"><CheckIcon isChecked={hasLowercaseLetters} /></span>
                          Al menos una minúscula (a-z)
                        </li>
                        <li className="flex items-center">
                          <span className="mr-1"><CheckIcon isChecked={hasCapitalLetters} /></span>
                          Al menos una mayúscula (A-Z)
                        </li>
                        <li className="flex items-center">
                          <span className="mr-1"><CheckIcon isChecked={hasNumbers} /></span>
                          Al menos un número (0-9)
                        </li>
                        <li className="flex items-center">
                          <span className="mr-1"><CheckIcon isChecked={hasSpecialCharacters} /></span>
                          <span
                            className="underline cursor-pointer"
                            data-tooltip-id="pwd-tooltip"
                            data-tooltip-place="top"
                            data-tooltip-content=" ^ $ * . [ ] { } ( ) ? &quot; - ! @ # % &amp; / \ , &gt; &lt; ' : ; | _ ~ ` + = "
                          >
                            Al menos un caracter especial
                          </span>
                          <Tooltip id="pwd-tooltip" />
                        </li>
                      </ul>
                    </div>

                    <InputForm
                      name="confirmPassword"
                      placeholder="••••••••"
                      type="password"
                      title="Confirmar contraseña"
                      value={confirmPassword}
                      setValue={setConfirmPassword}
                    />

                    <div className={`pl-4 ${getConfirmPasswordClass(password, confirmPassword)}`}>
                      <ul className="text-xs text-orange-400">
                        <li className="flex items-center">
                          <span className="mr-1"><CheckIcon isChecked={false} /></span>
                          Las contraseñas no coinciden
                        </li>
                      </ul>
                    </div>

                    {error && <ErrorAlert error={error} />}

                    <div>
                      <button
                        disabled={!isValidPassword}
                        onClick={handlePasswordResetSubmit}
                        className="w-full py-3 font-semibold rounded-full bg-black text-white border-2 border-orange-500 enabled:hover:bg-black/90 enabled:hover:border-orange-400 enabled:cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-orange-500 transition"
                      >
                        Restablecer contraseña
                      </button>
                    </div>

                    <hr className="border-zinc-700" />
                    <p className="text-sm flex justify-center font-light text-gray-400">
                      <Link to="/login" className="font-medium text-xs text-orange-500 hover:text-orange-400 transition">
                        Regresar a inicio de sesión
                      </Link>
                    </p>
                  </div>
                </div>
              )}

              {step === 3 && (
                <div className="text-center py-5 animate__animated animate__fadeIn">
                  <span className="text-2xl font-extrabold">¡Contraseña actualizada!</span>
                  <div className="flex justify-center my-5">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
                      className="w-28 h-28 fill-orange-500 animate__animated animate__flip">
                      <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z" clipRule="evenodd" />
                    </svg>
                  </div>

                  <div>
                    <h2 className="text-sm mb-7 text-gray-300">Tu contraseña ha sido actualizada exitosamente</h2>

                    <div className="h-2 w-full mb-2 bg-zinc-800 rounded-md overflow-hidden">
                      <div className="h-full bg-orange-500" style={{ width: `${progress}%` }}></div>
                    </div>
                    <p className="text-xs text-gray-400">En breve serás redirigido a la página de inicio de sesión</p>
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>
      </section>

      {/* Columna derecha (imagen) */}
      <section
        className="relative bg-cover bg-center bg-no-repeat w-1/2 h-full hidden lg:block"
        style={{ backgroundImage: `url(${bgImage})` }}
      >
        <div className="absolute inset-0 bg-black/50" />
      </section>
    </div>
  );
};

export default PasswordRecovery;
